import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UserSearchCard } from './UserSearchCard';
import { PostSearchCard } from './PostSearchCard';
import { useSearch } from '@/features/search/hooks/useSearch';
import { useI18n } from '@/contexts/I18nContext';
import BeltIcon from '@/assets/images/icons/belt.png';
import SearchIcon from '@/assets/icons/search.svg';

interface SearchResultsProps {
  query: string;
  userId?: string;
}

type TabType = 'all' | 'people' | 'posts';

export const SearchResults: React.FC<SearchResultsProps> = ({ query, userId }) => {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useSearch(query, activeTab, userId);
  const location = useLocation();
  const { t } = useI18n();
  
  // Flatten the pages data
  const results = data?.pages.flat() || [];

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Separar resultados por tipo
  const users = results.filter(result => result.type === 'user');
  const posts = results.filter(result => result.type === 'post');

  // Estado da busca atual para preservar na navegação
  const currentSearchState = {
    query,
    activeTab,
    results: results
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Tabs estilo My Journey */}
        <div className="flex gap-0 mb-2 border-b border-[#281B1B] w-full">
          <button 
            onClick={() => handleTabChange('all')} 
            className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'all' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
          >
            {t('search.all_results')}
          </button>
          <button 
            onClick={() => handleTabChange('people')} 
            className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'people' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
          >
            {t('search.users')}
          </button>
          <button 
            onClick={() => handleTabChange('posts')} 
            className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'posts' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
          >
            {t('search.posts')}
          </button>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 bg-white/5 rounded-lg animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/10 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-white/10 rounded w-32"></div>
                  <div className="h-3 bg-white/10 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <div className="space-y-4">
        {/* Tabs estilo My Journey */}
        <div className="flex gap-0 mb-2 border-b border-[#281B1B] w-full">
          <button 
            onClick={() => handleTabChange('all')} 
            className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'all' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
          >
            {t('search.all_results')}
          </button>
          <button 
            onClick={() => handleTabChange('people')} 
            className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'people' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
          >
            {t('search.users')}
          </button>
          <button 
            onClick={() => handleTabChange('posts')} 
            className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'posts' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
          >
            {t('search.posts')}
          </button>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          {/* Empty State Icon */}
          <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
            <img 
              src={SearchIcon} 
              alt="Search" 
              className="w-10 h-10 text-white/40"
              style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
            />
          </div>
          
          {/* Empty State Title */}
          <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
            {t('search.no_results_found')}
          </h3>
          
          {/* Empty State Subtitle */}
          <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
            {t('search.no_results_subtitle')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs estilo My Journey */}
      <div className="flex gap-0 mb-2 border-b border-[#281B1B] w-full">
        <button 
          onClick={() => handleTabChange('all')} 
          className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'all' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
        >
          {t('search.all_results')}
        </button>
        <button 
          onClick={() => handleTabChange('people')} 
          className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'people' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
        >
          {t('search.users')}
        </button>
        <button 
          onClick={() => handleTabChange('posts')} 
          className={`flex-1 pb-2 px-1 text-xs font-semibold ${activeTab === 'posts' ? 'text-white border-b-2 border-sossiety-red' : 'text-white/60 border-b-2 border-transparent'}`}
        >
          {t('search.posts')}
        </button>
      </div>

      {/* Conteúdo das abas */}
      {activeTab === 'all' && (
        <div className="space-y-6">
          {/* Seção Users */}
          {users.length > 0 && (
            <>
              <div>
                <h2 className="font-roboto font-extrabold text-[12px] text-white mb-4">{t('search.users')}</h2>
                <div className="space-y-4">
                  {users.map((user) => (
                    <UserSearchCard
                      key={`user-${user.id}`}
                      user={user}
                      searchState={currentSearchState}
                    />
                  ))}
                </div>
              </div>

              {/* Linha divisória com faixa */}
              {posts.length > 0 && (
                <div className="flex items-center my-6">
                  <div className="flex-1 h-px bg-white/20" />
                  <img src={BeltIcon} alt="Belt" className="mx-4 w-8 h-4 object-contain" />
                  <div className="flex-1 h-px bg-white/20" />
                </div>
              )}
            </>
          )}

          {/* Seção Posts */}
          {posts.length > 0 && (
            <div>
              <h2 className="font-roboto font-extrabold text-[12px] text-white mb-4">{t('search.posts')}</h2>
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostSearchCard
                    key={`post-${post.id}`}
                    post={post}
                    searchState={currentSearchState}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'people' && (
        <div className="space-y-4">
          {users.length > 0 ? (
            users.map((user) => (
              <UserSearchCard
                key={`user-${user.id}`}
                user={user}
                searchState={currentSearchState}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
              {/* Empty State Icon */}
              <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                <img 
                  src={SearchIcon} 
                  alt="Search" 
                  className="w-10 h-10 text-white/40"
                  style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
                />
              </div>
              
              {/* Empty State Title */}
              <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                {t('search.no_users_found')}
              </h3>
              
              {/* Empty State Subtitle */}
              <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                {t('search.no_users_subtitle')}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'posts' && (
        <div className="space-y-4">
          {posts.length > 0 ? (
            posts.map((post) => (
              <PostSearchCard
                key={`post-${post.id}`}
                post={post}
                searchState={currentSearchState}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
              {/* Empty State Icon */}
              <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
                <img 
                  src={SearchIcon} 
                  alt="Search" 
                  className="w-10 h-10 text-white/40"
                  style={{ filter: 'brightness(0) invert(1) opacity(0.4)' }}
                />
              </div>
              
              {/* Empty State Title */}
              <h3 className="text-xl font-semibold text-white mb-3 font-roboto">
                {t('search.no_posts_found')}
              </h3>
              
              {/* Empty State Subtitle */}
              <p className="text-white/60 text-sm max-w-sm leading-relaxed font-roboto">
                {t('search.no_posts_subtitle')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Load More Button */}
      {hasNextPage && (
        <div className="text-center pt-4">
          <button
            onClick={handleLoadMore}
            disabled={isFetchingNextPage}
            className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors disabled:opacity-50"
          >
            {isFetchingNextPage ? t('search.loading') : t('search.load_more')}
          </button>
        </div>
      )}
    </div>
  );
}; 